import { useEffect, useState } from 'react'
import { assertTitleTierSourceRecord } from '../../lib/titleTier.js'
import { api } from '../../lib/api.js'
import { titleTierFor } from '../../lib/titleTierView.js'

// A linked championship is read directly, never inferred from an artwork job's
// title/spec or a stale URL flag. This is a UX gate; the server enforces writes.
export function useChampionshipArtworkRouting(championshipId) {
  const id = String(championshipId || '')
  const [state, setState] = useState(null)
  const [revision, setRevision] = useState(0)
  useEffect(() => {
    if (!id) { setState(null); return undefined }
    let alive = true
    const controller = new AbortController()
    let request = 0
    const refresh = async () => {
      const ticket = ++request
      setState({ id, status: 'loading' })
      try {
        const response = await api.championshipRecord(id, { signal: controller.signal })
        if (!alive || ticket !== request) return
        assertTitleTierSourceRecord(response?.record, id)
        setState({ id, status: 'ready', routing: titleTierFor(response.record) })
      } catch (error) {
        if (alive && ticket === request && error.name !== 'AbortError') setState({ id, status: 'error', error: error.message })
      }
    }
    refresh()
    window.addEventListener('focus', refresh)
    return () => { alive = false; controller.abort(); window.removeEventListener('focus', refresh) }
  }, [id, revision])
  const current = state?.id === id ? state : null
  return {
    eligible: !id || (current?.status === 'ready' && current.routing.ringArtworkEligible),
    loading: Boolean(id && (!current || current.status === 'loading')),
    routing: current?.routing || null,
    reason: !id ? '' : current?.routing?.titleTierReason || (current?.status === 'error'
      ? `Award-level verification failed: ${current.error}. Automatic ring generation stays held; existing artwork uploads remain available.`
      : 'Checking the linked championship award level before automatic ring generation. Existing artwork uploads remain available.'),
    reload: () => setRevision(value => value + 1),
  }
}
